import { useState } from "react";
import {
  useSearchQuoteResolutionCandidates,
  getSearchQuoteResolutionCandidatesQueryKey,
  useGetQuoteCurationWorkspace,
  getGetQuoteCurationWorkspaceQueryKey,
  type QuoteResolutionCandidate,
} from "@workspace/api-client-react";
import { describeError, humanize, type CuratedQuote } from "./curationUtils";

function Facts({ q, label }: { q: CuratedQuote; label: string }) {
  return (
    <div className="min-w-0 flex-1 rounded-2xl border border-stone-200 bg-white p-3">
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-stone-400">{label} #{q.id}</p>
      <p className="line-clamp-4 font-serif text-sm leading-snug text-stone-800">&ldquo;{q.text}&rdquo;</p>
      <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs">
        <dt className="text-stone-400">Author</dt><dd className="truncate text-stone-700">{q.author}</dd>
        <dt className="text-stone-400">Work</dt><dd className="truncate text-stone-700">{q.work || "None"}</dd>
        <dt className="text-stone-400">Status</dt><dd className="text-stone-700">{humanize(q.status)}</dd>
        <dt className="text-stone-400">Visibility</dt><dd className="text-stone-700">{q.visibility ? humanize(q.visibility) : "Unknown"}</dd>
        <dt className="text-stone-400">Origin</dt><dd className="text-stone-700">{q.sourceType ? humanize(q.sourceType) : "Unknown"}</dd>
      </dl>
    </div>
  );
}

export function SurvivorPicker({ current, survivor, onSelect }: {
  current: CuratedQuote; survivor: QuoteResolutionCandidate | null; onSelect: (c: QuoteResolutionCandidate | null) => void;
}) {
  const [draft, setDraft] = useState("");
  const [submitted, setSubmitted] = useState("");
  const asId = /^\d+$/.test(submitted) ? Number(submitted) : undefined;
  const params = asId ? { quoteId: asId } : { query: submitted };
  const search = useSearchQuoteResolutionCandidates(params, {
    query: { queryKey: getSearchQuoteResolutionCandidatesQueryKey(params), enabled: submitted.length >= 1 },
  });
  const detail = useGetQuoteCurationWorkspace(survivor?.quote.id ?? 0, {
    query: { queryKey: getGetQuoteCurationWorkspaceQueryKey(survivor?.quote.id ?? 0), enabled: !!survivor },
  });
  const results = (search.data ?? []).filter((c) => c.quote.id !== current.id);

  return (
    <div className="space-y-3">
      <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); setSubmitted(draft.trim()); }}>
        <input aria-label="Search survivor by text, author or ID" placeholder="Search text, author or quote ID"
          value={draft} onChange={(e) => setDraft(e.target.value)}
          className="flex-1 rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-stone-400" />
        <button type="submit" className="rounded-xl bg-stone-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400">Search</button>
      </form>
      <p className="text-xs text-stone-400">Candidates are suggestions only. Nothing merges until you select a survivor, preview, and confirm.</p>

      {search.isFetching && <div className="h-16 animate-pulse rounded-2xl bg-stone-100" />}
      {search.isError && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{describeError(search.error)}</p>}
      {!search.isFetching && submitted && results.length === 0 && !search.isError && (
        <p className="rounded-2xl border border-dashed border-stone-200 p-4 text-center text-sm text-stone-400">No candidates match.</p>
      )}
      {results.length > 0 && (
        <ul className="max-h-52 space-y-1.5 overflow-y-auto" aria-label="Survivor candidates">
          {results.map((c) => {
            const active = survivor?.quote.id === c.quote.id;
            return (
              <li key={c.quote.id}>
                <button type="button" aria-pressed={active} onClick={() => onSelect(active ? null : c)}
                  className={`w-full rounded-2xl border p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 ${active ? "border-amber-300 bg-amber-50" : "border-stone-200 bg-white hover:border-stone-300"}`}>
                  <p className="line-clamp-2 font-serif text-sm text-stone-800">&ldquo;{c.quote.text}&rdquo;</p>
                  <p className="mt-1 text-xs text-stone-500">#{c.quote.id} · {c.quote.author} · {humanize(c.quote.status)}</p>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {survivor && (
        <div className="rounded-2xl bg-stone-50 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500">Side by side</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Facts q={current} label="Duplicate (removed)" />
            {detail.isLoading ? <div className="h-40 flex-1 animate-pulse rounded-2xl bg-stone-100" /> :
              <Facts q={(detail.data?.quote as CuratedQuote | undefined) ?? (survivor.quote as CuratedQuote)} label="Survivor (kept)" />}
          </div>
          {detail.data && (
            <p className="mt-2 text-xs text-stone-500">
              Survivor activity: {Object.entries(detail.data.counts).map(([k, v]) => `${humanize(k)} ${v}`).join(" · ") || "none"}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
